import { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { taskService } from '../services/taskService';
import { TASK_LIMITS } from '../constants/projectConstants';

const formatLocalYMD = (date) => {
  if (!date) return "";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const taskSchema = z
  .object({
    taskName: z
      .string()
      .min(3, "Task name must be at least 3 characters")
      .max(120, "Task name must be at most 120 characters"),
    description: z.string().max(2000).optional().or(z.literal('')),
    startDate: z.string().min(1, 'Start date is required'),
    estimatedEndDate: z.string().min(1, 'Estimated end date is required'),
    assignedOfficer: z.string().min(1, 'Please select an installation member')
  })
  .superRefine((data, ctx) => {
    const today = formatLocalYMD(new Date());
    if (data.startDate && data.startDate < today) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['startDate'],
        message: 'Start date cannot be in the past.'
      });
    }
    if (data.startDate && data.estimatedEndDate && data.estimatedEndDate < data.startDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['estimatedEndDate'],
        message: 'Estimated end date must be on or after the start date.'
      });
    }
  });

export const useScheduleTask = (project, onClose, onSuccess, taskToEdit = null) => {
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm({
    resolver: zodResolver(taskSchema),
    mode: 'onChange',
    defaultValues: {
      taskName: taskToEdit?.taskName || '',
      description: taskToEdit?.description || '',
      startDate: taskToEdit?.startDate ? formatLocalYMD(taskToEdit.startDate) : formatLocalYMD(new Date()),
      estimatedEndDate: taskToEdit?.estimatedEndDate ? formatLocalYMD(taskToEdit.estimatedEndDate) : formatLocalYMD(new Date()),
      assignedOfficer: taskToEdit?.assignedOfficer?._id || taskToEdit?.assignedOfficer || ''
    }
  });

  const resetForm = useCallback(() => {
    const today = formatLocalYMD(new Date());
    setFormError('');
    form.reset({
      taskName: taskToEdit?.taskName || '',
      description: taskToEdit?.description || '',
      startDate: taskToEdit?.startDate ? formatLocalYMD(taskToEdit.startDate) : today,
      estimatedEndDate: taskToEdit?.estimatedEndDate ? formatLocalYMD(taskToEdit.estimatedEndDate) : today,
      assignedOfficer: taskToEdit?.assignedOfficer?._id || taskToEdit?.assignedOfficer || ''
    });
  }, [form, taskToEdit]);

  const handleFormSubmit = async (data) => {
    setIsSubmitting(true);
    setFormError('');
    try {
      if (taskToEdit?._id) {
        await taskService.updateTask(taskToEdit._id, {
          ...data,
          description: data.description?.trim() || undefined,
          projectId: project._id
        });
      } else {
        await taskService.createTask({
          ...data,
          description: data.description?.trim() || undefined,
          projectId: project._id
        });
      }
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error('Error scheduling task:', error);
      const msg =
        typeof error === 'string'
          ? error
          : error?.message || (Array.isArray(error?.errors) ? error.errors.join(' ') : null) || 'Could not schedule task';
      setFormError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    form,
    formError,
    isSubmitting,
    handleFormSubmit,
    resetForm,
    formatLocalYMD
  };
};
